import type { Settings } from '@ais-ui/websockets';

import { addLayerBelowShips, removeFromLayerOrdering } from './layerOrdering';
import type { LayerOrder } from './types';

/**
 * All recognized layer types stored in layer_ordering.
 * Using a union type prevents typos and makes "add a new type" discoverable.
 */
export type LayerType =
  | 'vessels'
  | 'shape'
  | 'wms'
  | 'vectortile'
  | 'openseamap'
  | 'cambridge-radar'
  | 'coverage';

/** Well-known layer IDs used as constants to avoid magic strings. */
export const LAYER_IDS = {
  OPENSEAMAP: 'openseamap-seamark',
  COVERAGE: 'source-identifiers',
} as const;

/**
 * Get the layer ordering array from settings.
 * Single access point — no more repeating the deep property chain.
 */
export const getLayerOrdering = (settings: Settings): LayerOrder[] => {
  return settings?.['ribbonPlugin.settings']?.layer_ordering?.layers || [];
};

/**
 * Check if a layer is a startup favorite.
 * Replaces all type-specific isXxxShownOnStartUp functions.
 *
 * Checks layer_ordering first, falls back to legacy favorite_shapes
 * for backward compatibility with old saved settings.
 */
export const isLayerFavorite = (
  settings: Settings,
  layerId: string,
): boolean => {
  const ordering = getLayerOrdering(settings);
  if (ordering.some((layer) => layer.id === layerId)) {
    return true;
  }

  // Legacy fallback: shapes may only exist in favorite_shapes (pre-unification)
  if (isLegacyShapeFavorite(settings, layerId)) return true;

  // Legacy fallback: coverage may only have the boolean flag (pre-unification)
  if (layerId === 'source-identifiers') {
    return isLegacyCoverageFavorite(settings);
  }

  return false;
};

/**
 * Add a layer as a startup favorite.
 * Positions below ships layer. Dual-writes for shapes and coverage
 * to maintain backward compatibility with legacy storage.
 */
export const addFavoriteLayer = (
  settings: Settings,
  layerId: string,
  layerType: LayerType,
): Settings => {
  let updated: Settings = addLayerBelowShips(
    settings,
    layerType,
    layerId,
  ) as Settings;

  if (layerType === 'shape') {
    updated = addToLegacyShapeFavorites(updated, layerId);
  }

  if (layerType === 'coverage') {
    updated = setLegacyCoverageFlag(updated, true);
  }

  return updated;
};

/**
 * Remove a layer from startup favorites.
 * Dual-removes from legacy storage for shapes and coverage.
 */
export const removeFavoriteLayer = (
  settings: Settings,
  layerId: string,
  layerType: LayerType,
): Settings => {
  let updated: Settings = removeFromLayerOrdering(
    settings,
    layerId,
  ) as Settings;

  if (layerType === 'shape') {
    updated = removeFromLegacyShapeFavorites(updated, layerId);
  }

  if (layerType === 'coverage') {
    updated = setLegacyCoverageFlag(updated, false);
  }

  return updated;
};

// --- Legacy backward-compatibility helpers ---

const isLegacyShapeFavorite = (
  settings: Settings,
  shapeId: string,
): boolean => {
  return (
    settings?.['ribbonPlugin.settings']?.favorite_shapes?.shapes?.some(
      (id: string) => id === shapeId,
    ) ?? false
  );
};

const isLegacyCoverageFavorite = (settings: Settings): boolean => {
  return (
    settings?.['ribbonPlugin.settings']?.coverage_source_identifiers === true
  );
};

const addToLegacyShapeFavorites = (
  settings: Settings,
  shapeId: string,
): Settings => ({
  ...settings,
  'ribbonPlugin.settings': {
    ...settings['ribbonPlugin.settings'],
    favorite_shapes: {
      enabled: false,
      shapes: [
        ...(settings['ribbonPlugin.settings']?.favorite_shapes?.shapes || []),
        shapeId,
      ],
    },
  },
});

const removeFromLegacyShapeFavorites = (
  settings: Settings,
  shapeId: string,
): Settings => ({
  ...settings,
  'ribbonPlugin.settings': {
    ...settings['ribbonPlugin.settings'],
    favorite_shapes: {
      enabled: false,
      shapes:
        settings['ribbonPlugin.settings']?.favorite_shapes?.shapes?.filter(
          (id: string) => id !== shapeId,
        ) || [],
    },
  },
});

const setLegacyCoverageFlag = (
  settings: Settings,
  enabled: boolean,
): Settings => ({
  ...settings,
  'ribbonPlugin.settings': {
    ...settings['ribbonPlugin.settings'],
    coverage_source_identifiers: enabled,
  },
});
